/** Host OS — Memberships. Season-pass holders across every series you run. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Loader2, Repeat, ChevronDown, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import HostLayout from "@/components/host/HostLayout";
import { HostPageHeader, KpiCard, currencySymbol, formatMoneyMap } from "@/components/host/hostUi";

const initials = (n) => (!n ? "?" : n.split(" ").map((x) => x[0]).join("").toUpperCase().slice(0, 2));

const HostMembershipsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/memberships`, { headers });
      if (res.ok) setData(await res.json());
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const series = data?.series || [];
  const withMembers = series.filter((s) => s.active_members > 0 || s.cancelled_members > 0);

  return (
    <HostLayout>
      <div data-testid="host-memberships-page">
        <HostPageHeader
          icon={BadgeCheck}
          title="Memberships"
          subtitle="Season-pass holders across your recurring series — your most loyal, recurring revenue."
        />

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <KpiCard label="Active members" value={data?.total_active_members ?? 0} icon={BadgeCheck} accent="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" />
              <KpiCard label="Pass revenue" value={formatMoneyMap(data?.revenue_by_currency)} icon={Repeat} accent="bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300" />
            </div>

            {series.length === 0 ? (
              <Card className="p-10 text-center border-dashed" data-testid="memberships-empty">
                <BadgeCheck className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="font-medium">No series yet</p>
                <p className="text-xs text-muted-foreground mt-1 mb-4">Recurring series let players buy a season pass — the most reliable revenue you can build.</p>
                <Button onClick={() => navigate("/create/series")} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5">
                  <Repeat className="w-4 h-4" /> Start a series
                </Button>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="memberships-list">
                {series.map((s) => {
                  const isOpen = !!open[s.series_id];
                  return (
                    <Card key={s.series_id} className="overflow-hidden" data-testid={`membership-series-${s.series_id}`}>
                      <button
                        onClick={() => setOpen((p) => ({ ...p, [s.series_id]: !p[s.series_id] }))}
                        className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/20 transition-colors"
                        data-testid={`membership-toggle-${s.series_id}`}
                      >
                        <img src={s.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-display text-lg leading-tight truncate">{s.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {s.active_members} active{s.cancelled_members > 0 ? ` · ${s.cancelled_members} lapsed` : ""} · {s.total_sessions} sessions
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold">{currencySymbol(s.currency)}{Math.round(s.revenue).toLocaleString()}</p>
                          <p className="text-[10px] text-muted-foreground">pass {currencySymbol(s.currency)}{Math.round(s.pass_price)}</p>
                        </div>
                        {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                      </button>

                      {isOpen && (
                        <div className="border-t border-border">
                          {s.members.length === 0 ? (
                            <p className="text-sm text-muted-foreground p-4 text-center">No active members yet.</p>
                          ) : (
                            <div className="divide-y divide-border">
                              {s.members.map((m, i) => (
                                <button
                                  key={m.ref_id || i}
                                  onClick={() => m.user_id && navigate(`/profile/${m.user_id}`)}
                                  className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/30 transition-colors"
                                  data-testid={`member-row-${s.series_id}-${i}`}
                                >
                                  <Avatar className="w-9 h-9 shrink-0 ring-1 ring-border">
                                    <AvatarImage src={m.user_picture} alt={m.user_name} />
                                    <AvatarFallback className="bg-primary/20 text-primary-ink text-xs">{initials(m.user_name)}</AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{m.user_name}</p>
                                    <p className="text-xs text-muted-foreground truncate">{m.user_email}</p>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <p className="text-sm font-semibold">{currencySymbol(m.currency)}{Math.round(m.amount_paid)}</p>
                                    {m.sessions_remaining != null && (
                                      <p className="text-[10px] text-muted-foreground">{m.sessions_remaining}/{m.sessions_total} left</p>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </HostLayout>
  );
};

export default HostMembershipsPage;
